import React, { type ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { Alert, AlertTitle, Grid, Slide } from "@mui/material";

import { ToastTimerBar } from "../components";

import { ToastContext, type Toast } from "./toast.context";

interface ToastInstance extends Toast {
	id: number;
	show: boolean;
	timeout: number;
}

interface ToastsProviderProps {
	readonly children: ReactNode;
	readonly toastTimeout?: number;
	readonly clearTimeout?: number;
}

const DEFAULT_TOAST_TIMEOUT = 5000;
const DEFAULT_CLEAR_TIMEOUT = 1000;

export const ToastProvider: React.FC<ToastsProviderProps> = ({ children, toastTimeout, clearTimeout }) => {
	const [toasts, setToasts] = useState<ToastInstance[]>([]);

	const clearToasts = useCallback(() => {
		setToasts((prevToasts) => {
			if (prevToasts.some((t) => t.show)) {
				return prevToasts;
			}

			return [];
		});
	}, []);

	const hideToast = useCallback(
		(id: number) => {
			setToasts((prevToasts) =>
				prevToasts.map((toast) => {
					if (toast.id === id) {
						return { ...toast, show: false };
					}

					return toast;
				}),
			);

			setTimeout(() => {
				clearToasts();
			}, clearTimeout || DEFAULT_CLEAR_TIMEOUT);
		},
		[clearTimeout, clearToasts],
	);

	const addToast = useCallback(
		(data: Toast) => {
			const timeout = data.timeout ?? toastTimeout ?? DEFAULT_TOAST_TIMEOUT;

			const newToast: ToastInstance = {
				id: Date.now(),
				show: true,
				...data,
				timeout,
			};

			setToasts((prevToasts) => [...prevToasts, newToast]);

			setTimeout(() => {
				hideToast(newToast.id);
			}, timeout);
		},
		[hideToast, toastTimeout],
	);

	const handleToastClick = async (toast: ToastInstance) => {
		if (toast.onClick) {
			try {
				const result = await toast.onClick();
				hideToast(toast.id);
				return result;
			} catch (e) {
				console.error(e);
			}
		}

		hideToast(toast.id);
	};

	useEffect(() => {
		return () => {
			setToasts([]);
		};
	}, []);

	const value = useMemo(
		() => ({
			addToast,
			removeToast: clearToasts,
		}),
		[addToast, clearToasts],
	);

	return (
		<ToastContext.Provider value={value}>
			<Grid
				sx={{ position: "absolute", top: 24, left: 0, overflow: "hidden", alignItems: "flex-start" }}
				spacing={0.5}
				container
				direction="column"
			>
				{toasts.map((toast) => (
					<Slide key={toast.id} direction="right" in={toast.show} timeout={300}>
						<Alert
							sx={{
								cursor: "pointer",
								zIndex: 10000,
								marginLeft: 3,
								position: "relative",
								overflow: "hidden",
								width: "fit-content",
								maxWidth: "calc(100vw - 48px)",
							}}
							severity={toast.type}
							variant="filled"
							onClick={() => handleToastClick(toast)}
							data-testid={toast.testId ?? "toast-provider__toast__container"}
						>
							{Boolean(toast.title) && (
								<AlertTitle data-testid="toast-provider__toast__title">{toast.title}</AlertTitle>
							)}
							{toast.message}
							<ToastTimerBar severity={toast.type} timeout={toast.timeout} />
						</Alert>
					</Slide>
				))}
			</Grid>

			{children}
		</ToastContext.Provider>
	);
};
